import {
    MAT_DIALOG_DATA,
    MatDialogContent,
    MatDialogModule,
    MatDialogRef,
    MatDialogTitle
} from "@angular/material/dialog";
import { ChangeDetectionStrategy, Component, Inject, OnInit } from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { RecurrenceType } from "../../../polls/create-poll/create-poll.type";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { PollResultTask } from "../../taskbook.types";
import { distinctUntilChanged, tap } from "rxjs";
import { UserPollsService } from "../../../polls/user-polls/user-polls.service";
import { CommonModule } from "@angular/common";

@Component({
    selector: 'poll-task-recurrence-dialog',
    templateUrl: './poll-task-recurrence-dialog.component.html',
    styleUrls: ['./poll-task-recurrence-dialog.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        TranslateModule,
        MatDialogModule,
        MatDialogTitle,
        MatDialogContent,
        ReactiveFormsModule,
        FormsModule,
    ]
})
export class PollTaskRecurrenceDialogComponent implements OnInit {
    
    protected recurrenceOptions: RecurrenceType[] = ['weekly', 'monthly'];
    protected teamScopes: {
        value: 'current' | 'all',
        label: string
    }[] = [
        {
            value: 'all',
            label: this.translate.instant('polls_settings.make_recurrent.scope.all')
        },
        {
            value: 'current',
            label: this.translate.instant('polls_settings.make_recurrent.scope.current')
        },
    ]
    protected selectedScope = {
        value: 'all',
        label: this.translate.instant('polls_settings.make_recurrent.scope.all')
    };

    recurrenceFormGroup = new FormGroup({
        frequencyCtrl: new FormControl<RecurrenceType>('once', [Validators.required]),
        displayDurationCtrl: new FormControl<number>(10, [Validators.required]),
    })
    get frequencyCtrl() {
        return this.recurrenceFormGroup.get('frequencyCtrl');
    }
    get displayDurationCtrl() {
        return this.recurrenceFormGroup.get('displayDurationCtrl');
    }

    protected task: PollResultTask;
    protected recurrenceErrorMessage: string;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: PollRecurrentData,
        public dialogRef: MatDialogRef<PollTaskRecurrenceDialogComponent>,
        protected translate: TranslateService,
        protected userPollsService: UserPollsService
    ) { }

    ngOnInit() {
        this.task = this.data.task;

        this.frequencyCtrl.valueChanges.pipe(
            distinctUntilChanged(),
            tap((frequency) => {
                this.task.recurrence.frequency = frequency;
                const currentDuration = this.displayDurationCtrl.getRawValue();
                switch(frequency){
                    case 'once':
                        this.displayDurationCtrl.setValidators([Validators.required, Validators.min(1), Validators.max(30)]);
                        this.recurrenceErrorMessage = this.translate.instant('poll_creation.schedule_duration_error');
                        this.displayDurationCtrl.setValue(currentDuration); //needed to clear error message
                        break;
                    case 'weekly':
                        this.displayDurationCtrl.setValidators([Validators.required, Validators.min(1), Validators.max(7)]);
                        this.recurrenceErrorMessage = this.translate.instant('polls_settings.make_recurrent.duration_error.weekly');
                        if(currentDuration > 7){
                            this.displayDurationCtrl.setValue(7);
                        }
                        break;
                    case 'monthly':
                        this.displayDurationCtrl.setValidators([Validators.required, Validators.min(1), Validators.max(15)]);
                        this.recurrenceErrorMessage = this.translate.instant('polls_settings.make_recurrent.duration_error.monthly')
                        if(currentDuration > 15){
                            this.displayDurationCtrl.setValue(15);
                        }
                        break;
                }
            })
        ).subscribe();

        this.displayDurationCtrl.valueChanges.pipe(
            distinctUntilChanged(),
            tap((duration) => {
                this.task.recurrence.duration = duration;
            })
        ).subscribe();

        this.frequencyCtrl.setValue('weekly');
    }

    updateTeamScope(scope) {
        this.selectedScope = scope;
        switch(scope){
            case 'current':
                this.task.recurrence.pollTargets [this.task.team.id];
                break;
            case 'all':
                this.task.recurrence.pollTargets = this.task.pollTargets.map(t => t.id);
                break;
        }
    }

    close() {
        this.dialogRef.close();
    }
    setRecurrence() {
        this.dialogRef.close(this.task);
    }
}

export interface PollRecurrentData {
    task: PollResultTask;
}